import { Button, Chip } from '../hybrid'
import { labelForReadinessStatus, toneForReadinessStatus } from './home-signal-helpers'
import type { ReadinessCheck } from '@agentwiki/shared'

export interface HomeActivityItem {
  time: string
  text: string
}

interface HomeMcpHealth {
  isLoading: boolean
  isError: boolean
  status?: 'ready' | 'unreachable'
}

const defaultMcpHealth: HomeMcpHealth = {
  isLoading: false,
  isError: false,
}

export function HomeInspector({
  processingCount,
  failedCount,
  uploadStatusUnavailable = false,
  activityItems = [],
  activityUnavailable = false,
  searchReadinessCheck,
  mcpHealth = defaultMcpHealth,
  storageEnabled = true,
}: {
  processingCount: number
  failedCount: number
  uploadStatusUnavailable?: boolean
  activityItems?: HomeActivityItem[]
  activityUnavailable?: boolean
  searchReadinessCheck?: ReadinessCheck
  mcpHealth?: HomeMcpHealth
  storageEnabled?: boolean
}) {
  const searchChip = searchReadinessCheck
    ? {
        tone: toneForReadinessStatus(searchReadinessCheck.status),
        label: labelForReadinessStatus(searchReadinessCheck.status),
      }
    : { tone: 'mut' as const, label: 'Not tested' }
  const mcpChip = mcpHealth.isLoading
    ? { tone: 'mut' as const, label: 'checking' }
    : mcpHealth.isError
      ? { tone: 'dgr' as const, label: 'offline' }
      : {
          tone: mcpHealth.status === 'ready' ? ('ok' as const) : ('dgr' as const),
          label: mcpHealth.status === 'ready' ? 'online' : 'offline',
        }

  return (
    <aside className="aw-home-inspector">
      <div className="aw-home-inspector-head">Workspace activity</div>
      <div className="aw-home-inspector-block">
        <span className="aw-label">Live feed</span>
        <div style={{ marginTop: 10 }}>
          {activityItems.length > 0 ? (
            activityItems.map((event) => (
              <div key={`${event.time}-${event.text}`} className="aw-feed">
                <span className="aw-feed-time">{event.time}</span>
                <span className="aw-feed-body">{event.text}</span>
              </div>
            ))
          ) : activityUnavailable ? (
            <div className="aw-empty-row">Activity feed requires audit access</div>
          ) : (
            <div className="aw-empty-row">No audited tenant activity yet</div>
          )}
        </div>
      </div>
      <div className="aw-home-inspector-block">
        <span className="aw-label">Pipeline health</span>
        <dl style={{ marginTop: 10 }}>
          {storageEnabled && (
            <div className="aw-dl-row">
              <dt>Extraction queue</dt>
              <dd>
                <Chip tone={uploadStatusUnavailable ? 'warn' : 'proc'}>
                  {uploadStatusUnavailable ? 'Unavailable' : `${processingCount} running`}
                </Chip>
              </dd>
            </div>
          )}
          {storageEnabled && (
            <div className="aw-dl-row">
              <dt>Failed sources</dt>
              <dd>
                <Chip tone={uploadStatusUnavailable ? 'warn' : 'dgr'}>
                  {uploadStatusUnavailable ? 'Unavailable' : failedCount}
                </Chip>
              </dd>
            </div>
          )}
          <div className="aw-dl-row">
            <dt>Search index</dt>
            <dd>
              <Chip tone={searchChip.tone}>{searchChip.label}</Chip>
            </dd>
          </div>
          <div className="aw-dl-row">
            <dt>MCP endpoint</dt>
            <dd>
              <Chip tone={mcpChip.tone}>{mcpChip.label}</Chip>
            </dd>
          </div>
        </dl>
      </div>
      <div style={{ padding: '16px 20px' }}>
        <Button to="/agents" className="w-full">
          Open agent access
        </Button>
      </div>
    </aside>
  )
}
